import type { FormEvent } from 'react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, ChangeText, EmptyState, LiveNumber, Skeleton, useToast } from '../../ui'
import { useLiveIndices, useLiveStocks } from '../../lib/liveMarket'
import { api, post } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import { CONFIG_CHANGED } from '../../lib/config'
import { Pill, ago, errText, when, useAdmin } from './shared'
import { feedTone } from './OverviewTab'
import type { MarketBlock } from './OverviewTab'
import styles from './admin.module.css'

interface Q { ltp: number; change: number | null; change_pct: number | null; prev_close?: number | null; open?: number | null; high?: number | null; low?: number | null; volume?: number | null; week52_high?: number | null; week52_low?: number | null }
interface TickStatus { connected: boolean; subscribed: number; instruments_ticking: number; last_tick_age_s: number | null; ticks_per_min: number; connects: number; last_error: string | null; flush_ms: number }
interface Market extends MarketBlock {
  ticks?: TickStatus
  status: { state: string; label: string; mcx: string }
  token: { valid: boolean; has_token: boolean; expires_at: string | null; expires_in_h: number | null; set_at: string | null; client_id: string } | null
  universe: { label: string; top: number; total: number }
  indices: Array<Q & { name: string }>
  commodities: Array<Q & { name: string; expiry: string }>
  stocks: Array<Q & { symbol: string; name: string | null; sector: string | null; cap: string | null }>
}
interface QuoteResult { symbol: string; company: { name: string; sector: string | null } | null; snapshot: Q | null; snapshot_at: string | null; live: (Record<string, unknown> & { ltp?: number; source?: string; error?: string }) | null }
type SortKey = 'symbol' | 'ltp' | 'change_pct' | 'volume'

export function LiveRatesTab() {
  const toast = useToast()
  const { data: m, error, reload } = useAdmin<Market>('/admin/market', 10000)
  const [busy, setBusy] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'change_pct', dir: -1 })
  const [sym, setSym] = useState('')
  const [quote, setQuote] = useState<QuoteResult | null>(null)
  const live = useLiveStocks()
  const liveIdx = useLiveIndices()

  const act = async (name: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(name)
    try { await fn(); toast.show({ tone: 'success', message: ok }); await reload() }
    catch (e) { toast.show({ tone: 'error', message: errText(e) }) }
    finally { setBusy(null) }
  }
  const toggle = (enabled: boolean | null) => act('toggle', async () => { await post('/admin/market/toggle', { enabled }); window.dispatchEvent(new Event(CONFIG_CHANGED)) },
    enabled === null ? 'Live rates now follow .env' : enabled ? 'Live rates switched ON for everyone' : 'Live rates switched OFF for everyone')
  const refresh = () => act('refresh', async () => { const r = await post<{ ok: boolean }>('/admin/market/refresh', {}); if (!r.ok) throw new Error('Dhan did not answer — see the last error below') }, 'Rates refreshed from Dhan')
  const renewToken = () => {
    if (!window.confirm('Generate a new Dhan access token now (PIN + TOTP)? The old app does this automatically at 08:15 IST.')) return
    void act('token', async () => { const r = await post<{ success: boolean; message: string }>('/admin/market/token-refresh', {}); if (!r.success) throw new Error(r.message) }, 'New Dhan token saved')
  }
  const lookup = (e: FormEvent) => {
    e.preventDefault()
    const s = sym.trim().toUpperCase()
    if (!s) return
    setBusy('quote')
    api<QuoteResult>(`/admin/market/quote/${encodeURIComponent(s)}`).then(setQuote).catch(err => toast.show({ tone: 'error', message: errText(err) })).finally(() => setBusy(null))
  }

  const rows = useMemo(() => {
    const f = filter.trim().toUpperCase()
    const list = (m?.stocks ?? []).map(s => { const l = live(s.symbol); return l ? { ...s, ...l, volume: l.volume ?? s.volume } : s }).filter(s => !f || s.symbol.includes(f) || (s.name ?? '').toUpperCase().includes(f) || (s.sector ?? '').toUpperCase().includes(f))
    return list.sort((a, b) => {
      const av = a[sort.key] ?? -Infinity, bv = b[sort.key] ?? -Infinity
      return (typeof av === 'string' ? String(av).localeCompare(String(bv)) : Number(av) - Number(bv)) * sort.dir
    })
  }, [m, filter, sort, live])

  if (error && !m) return <Card variant="flush"><EmptyState kind="error" title="Could not load live rates" compact>{error}</EmptyState></Card>
  if (!m) return <Card><Skeleton lines={5} label="Loading live rates" /></Card>
  const [tone, word] = feedTone(m)
  const failing = m.feed.last_error_at && (!m.feed.last_ok_at || m.feed.last_error_at > m.feed.last_ok_at)
  const th = (key: SortKey, label: string, num = false) => (
    <th scope="col" className={num ? styles.thNum : undefined} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => setSort(s => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === 'symbol' ? 1 : -1 }))}>{label}{sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}</button>
    </th>
  )

  return (
    <div className={styles.section}>
      <div className={styles.hero}>
        <Card title="Live rates switch">
          <div className={styles.switchCard}>
            <div className={styles.switchRow}>
              <button type="button" role="switch" aria-checked={m.enabled} aria-label="Live rates for all users" className={styles.switch}
                disabled={busy === 'toggle'} onClick={() => void toggle(!m.enabled)} />
              <span className={styles.bigState}>{m.enabled ? 'ON — prices shown to users' : 'OFF — prices hidden everywhere'}</span>
              <Pill tone={tone}>{word}</Pill>
            </div>
            <p className={styles.muted} style={{ margin: 0 }}>
              Controls the price strip, market breadth, gainers/losers, sector moves, watchlist and holdings prices and stock quotes for every user.
              {m.source === 'admin' ? <> Set here (overrides .env). <button type="button" className={styles.linkBtn} onClick={() => void toggle(null)}>Follow .env again</button></> : ' Following PRICES_ENABLED in .env.'}
            </p>
            {failing && <p className={`${styles.notice} ${styles.noticeBad}`}>Last poll failed {when(m.feed.last_error_at)}: {m.feed.last_error}</p>}
            {m.token && !m.token.valid && <p className={`${styles.notice} ${styles.noticeBad}`}>Dhan token is invalid — renew it below, or wait for the 08:15 IST auto-renewal.</p>}
            <div className={styles.toolbar}>
              <Button variant="primary" onClick={() => void refresh()} loading={busy === 'refresh'} disabled={!m.enabled}>Refresh rates now</Button>
              <Button onClick={() => void renewToken()} loading={busy === 'token'}>Renew Dhan token</Button>
            </div>
          </div>
        </Card>
        <Card title="Feed status">
          <dl className={styles.facts}>
            <dt>Market</dt><dd>{m.status.label} · MCX {m.status.mcx}</dd>
            <dt>Real-time feed</dt><dd>{m.ticks ? `${m.ticks.connected ? `WebSocket connected · ${m.ticks.instruments_ticking}/${m.ticks.subscribed} ticking · ${m.ticks.ticks_per_min.toLocaleString('en-IN')} ticks/min · last ${m.ticks.last_tick_age_s ?? '—'} s ago` : `DISCONNECTED${m.ticks.last_error ? ` — ${m.ticks.last_error}` : ''}`}` : '—'}</dd>
            <dt>Last update</dt><dd>{m.snapshot_at ? `${when(m.snapshot_at)} (${ago(m.snapshot_age_s)})` : '—'}</dd>
            <dt>REST refresh</dt><dd>{m.feed.next_poll_ms ? `${Math.round(m.feed.next_poll_ms / 1000)} s` : '—'}{m.feed.took_ms ? ` · last call ${m.feed.took_ms} ms` : ''}</dd>
            <dt>Instruments</dt><dd>{m.feed.instruments || '—'} ({m.universe.top} top-500 + {m.universe.total - m.universe.top} watchlist/holding)</dd>
            <dt>Polls</dt><dd>{m.feed.polls_ok} ok · {m.feed.polls_failed} failed (since restart)</dd>
            <dt>Dhan token</dt><dd>{m.token ? `${m.token.valid ? 'valid' : 'INVALID'} · expires ${when(m.token.expires_at)}${m.token.expires_in_h != null ? ` (${m.token.expires_in_h} h)` : ''}` : '—'}</dd>
            <dt>Source</dt><dd>Dhan REST /v2/marketfeed/quote · client {m.token?.client_id ?? '—'}</dd>
          </dl>
        </Card>
      </div>

      <h2 className={styles.h2}>Indices and MCX</h2>
      {m.indices.length + m.commodities.length === 0 ? <Card variant="flush"><EmptyState kind="not-connected" title="No rates yet" compact>{m.enabled ? 'Waiting for the first poll.' : 'Switch live rates on to start polling Dhan.'}</EmptyState></Card> : (
        <div className={styles.tickers}>
          {[...m.indices.map(i => ({ ...i, ...liveIdx.indices.find(x => x.name === i.name), sub: 'Index' })), ...m.commodities.map(c => ({ ...c, ...liveIdx.mcx.find(x => x.name === c.name), sub: `MCX · exp ${c.expiry}` }))].map(t => (
            <div key={t.name} className={styles.ticker}>
              <span className={styles.tickerName}>{t.name} <span className={styles.muted}>· {t.sub}</span></span>
              <LiveNumber className={styles.tickerValue} value={t.ltp} />
              <ChangeText absolute={t.change} percent={t.change_pct} size="sm" />
            </div>
          ))}
        </div>
      )}

      <Card title="Check any symbol">
        <form onSubmit={lookup} className={styles.toolbar}>
          <label htmlFor="admin-quote" className="sr-only">NSE symbol</label>
          <input id="admin-quote" className={styles.input} placeholder="NSE symbol, e.g. TCS" value={sym} onChange={e => setSym(e.target.value)} autoCapitalize="characters" />
          <Button type="submit" variant="primary" loading={busy === 'quote'} disabled={!sym.trim()}>Get live quote</Button>
          {quote && <Link to={`/stock/${encodeURIComponent(quote.symbol)}`}>Open stock page →</Link>}
        </form>
        {quote && (
          <>
            <p style={{ margin: '12px 0 0' }}><b>{quote.symbol}</b> {quote.company ? <span className={styles.muted}>· {quote.company.name}{quote.company.sector ? ` · ${quote.company.sector}` : ''}</span> : <span className={styles.muted}>· not in company master</span>}</p>
            {quote.live?.error ? <p className={`${styles.notice} ${styles.noticeBad}`} style={{ marginTop: 8 }}>Dhan: {quote.live.error}</p> : quote.live?.ltp ? (
              <div className={styles.quoteBox}>
                <div><span>LTP (direct call)</span><span>{formatNumber(Number(quote.live.ltp))}</span></div>
                <div><span>Source</span><span>{String(quote.live.source ?? '—')}</span></div>
                {(['open', 'high', 'low', 'prevClose', 'volume'] as const).map(k => quote.live?.[k] != null ? <div key={k}><span>{k === 'prevClose' ? 'Prev close' : k.charAt(0).toUpperCase() + k.slice(1)}</span><span>{formatNumber(Number(quote.live[k]), k === 'volume' ? 0 : 2)}</span></div> : null)}
                <div><span>In snapshot</span><span>{quote.snapshot ? `${formatNumber(quote.snapshot.ltp)} (${quote.snapshot.change_pct ?? '—'}%)` : 'no'}</span></div>
              </div>
            ) : <p className={styles.muted}>No quote returned.</p>}
          </>
        )}
      </Card>

      <Card title={`All stocks in the feed (${m.stocks.length})`} variant="flush" action={
        <><label htmlFor="admin-rate-filter" className="sr-only">Filter stocks</label><input id="admin-rate-filter" className={styles.input} placeholder="Filter symbol, name, sector" value={filter} onChange={e => setFilter(e.target.value)} /></>
      }>
        {rows.length === 0 ? <EmptyState title={m.stocks.length ? 'No match' : 'No stock rates yet'} compact /> : (
          <div className={styles.tableWrap} style={{ maxHeight: 560, overflowY: 'auto' }} tabIndex={0} role="region" aria-label="Stock rates (scrolls)">
            <table className={styles.table}><caption className="sr-only">Live stock rates</caption>
              <thead><tr>{th('symbol', 'Symbol')}<th scope="col">Company</th><th scope="col">Sector</th>{th('ltp', 'LTP', true)}{th('change_pct', 'Change', true)}<th scope="col" className={styles.thNum}>Day range</th>{th('volume', 'Volume', true)}</tr></thead>
              <tbody>{rows.slice(0, 600).map(s => (
                <tr key={s.symbol}>
                  <td><Link className={styles.sym} to={`/stock/${encodeURIComponent(s.symbol)}`}>{s.symbol}</Link></td>
                  <td>{s.name ?? '—'}</td><td className={styles.muted}>{s.sector ?? '—'}{s.cap ? ` · ${s.cap}` : ''}</td>
                  <td className={styles.num}><LiveNumber value={s.ltp} /></td>
                  <td className={styles.num}><ChangeText absolute={s.change} percent={s.change_pct} size="sm" /></td>
                  <td className={styles.num}>{s.low != null && s.high != null ? `${formatNumber(s.low)} – ${formatNumber(s.high)}` : '—'}</td>
                  <td className={styles.num}>{s.volume ? formatNumber(s.volume, 0) : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
